<script setup>
import Vue, {computed, onMounted, reactive, ref, watch} from "vue";
import useForm from "@/composables/useForm";
import {useStore} from "vue2-helpers/vuex";
import inviteService from "@/services/invite.service";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import {
    accountTypePremium,
    accountTypeStandard, companyTypeEcosystem,
    companyTypeEnterprise
} from "@/constants/accountTypes";
import useNeedUpgrade from "@/composables/useNeedUpgrade";
import ecosystemStartupsService from "@/services/ecosystem/startups.service";
import constants from "@/constants/constants";
import customFieldsService from "@/services/custom-fields.service";
import CustomFieldValue from "@/components/customFields/CustomFieldValue.vue";
import useErrorsArray from "@/composables/useErrorsArray";
import TrackersSelect from "@/components/manage/TrackersSelect.vue";
import TagsSelect from "@/components/manage/TagsSelect.vue";
import PortfoliosSelect from "@/components/manage/PortfoliosSelect.vue";
const {showNeedUpgrade} = useNeedUpgrade();
import {baseURL} from "@/axios";
import EmailTemplateEdit from "@/components/settings/EmailTemplateEdit.vue";
import companyService from "@/services/company.service";
import FailedAddStartupsModal from "@/components/startups/FailedAddStartupsModal.vue";
import AddStartupsPreviewModal from "@/components/startups/import/AddStartupsPreviewModal.vue";
import {createCSV} from "@/helpers/file";
import {createStartupLabel} from "@/helpers/string";

defineProps({
    allowMultiple: {
        type: Boolean,
        default: true,
    }
});

const store = useStore();
const emit = defineEmits('startupsAdded', 'closed');

const form = reactive({
    name: '',
    url: '',
    contactEmail: '',
    isPublic: true,
    autoTagging: false,
    customFields: [],
    tags: [],
    trackers: [],
    portfolios: [],
    emailTemplate: {
        subject: "",
        body: "",
    },
})

const {resetForm, errors, setErrors, hasErrors, clearErrors} = useForm(form);

const usersCanSoftTagging = [
    'rdoshi4@travelers.com',
    'cfoster9@travelers.com',
    'sliberat@travelers.com',
    'adutt@travelers.com',
    'talauret@travelers.com',
    'mag+travelers@switchpitch.com',
    'anton@switchpitch.com',
    'cinzia+travelers@switchpitch.com',
    'maxmms3+travellers@gmail.com' // Test account
];

const showModal = ref(false);
const multiple = ref(false);
const loading = ref(false);
const canInvite = ref(false);
const trackers = ref([]);
const tags = ref([]);
const portfolios = ref([]);

const upload = ref(null);
const file = ref(null);
const fileErrors = ref([]);
const emailTemplateEditModal = ref(null);
const failedAddStartupsModal = ref(null);
const previewModal = ref(null);

const {errors: customFieldsErrors, populate: populateCustomFieldsErrors, hasErrors: hasCustomFieldsErrors, clear: clearCustomFieldsErrors} = useErrorsArray();

const canAdd = computed(() => {
    if (multiple.value) {
        return file.value;
    } else {
        return !!form.name.length && (!!form.url.length || !form.isPublic);
    }
});

const showTags = computed(() => store.state.companyType === companyTypeEnterprise && store.getters.settings.show_tags_on_add_startup_form);
const contactEmailEntered = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail));

watch(showModal, value => {
    if (!value) {
        emit('closed');
    }
});

watch(trackers, value => {
    form.trackers = value.map(item => item.id);
});

watch(tags, value => {
    form.tags = value.map(item => item.id);
});

watch(portfolios, value => {
    form.portfolios = value.map(item => item.id);
});

onMounted(() => {
    store.dispatch('fetchTrackers');
})

function getData() {
    if (store.state.accountType === accountTypePremium) {
        if (!store.getters.isTravelersEnterprise) {
            canInvite.value = true;
        }

        showModal.value = true;
    } else {
        if (store.state.companyType === companyTypeEcosystem) {
            checkEcosystemCanAdd();
        } else {
            showNeedUpgrade('startups-add');
        }
    }

    if (store.state.companyType === companyTypeEnterprise) {
        if (store.getters.settings.show_tags_on_add_startup_form) {
            getCustomFields();
        }

        if (store.getters.isPfizerEnterprise) {
            form.isPublic = false;
        }
    }
}

async function checkEcosystemCanAdd() {
    const {data} = await ecosystemStartupsService.getCounts();

    if (data.added < constants.ecosystemLimits['startups-add'][store.state.accountType]) {
        showModal.value = true;
    } else {
        showNeedUpgrade('startups-add');
    }

    if (store.state.accountType === accountTypeStandard && data.added < constants.ecosystemLimits['startups-invite'][store.state.accountType]) {
        canInvite.value = true;
    }
}

async function getCustomFields() {
    const {data} = await customFieldsService.getFieldsForStartupAddForm();

    form.customFields = data.map(item => ({
        field: item,
        value: {
            text: '',
            options: [],
            files: [],
            links: [],
        },
    }));
}

function customValueChanged(index, value) {
    form.customFields[index].value = value;
}

async function add() {
    clearErrors();
    clearCustomFieldsErrors();

    try {
        if (multiple.value) {
            loading.value = true;
            fileErrors.value = [];

            let formData = new FormData();

            formData.append('file', file.value);
            formData.append('preview', true);
            formData.append('is_public', form.isPublic ? 1 : 0);
            formData.append('auto_tagging', form.autoTagging ? 1 : 0);

            const {data} = await inviteService.addMultipleStartups(formData);

            fileErrors.value = data.errors?.file ?? [];

            if (!fileErrors.value.length) {
                previewModal.value.open(data);
            }
        } else {
            if ((!contactEmailEntered.value || confirm(form.name + " will be invited to join SwitchPitch, do you want to proceed?"))) {
                loading.value = true;
                const {data} = await inviteService.addSingleStartup(form);

                setErrors(data.errors);
                populateCustomFieldsErrors(data.errors, 'customFields-', form.customFields.length);

                if (!hasErrors(data) && !hasCustomFieldsErrors.value) {
                    showModal.value = false;
                    emit('startupsAdded', data.startup);

                    Vue.notify({
                        group: 'app',
                        type: data.success ? 'success' : 'error',
                        duration: data.success ? 15000 : 5000,
                        text: data.result
                    })
                }
            }
        }
    } finally {
        loading.value = false;
    }
}

async function sendImport(importedData) {
    loading.value = true;
    const chunkSize = 10;
    const chunks = [];

    for (let i = 0; i < (importedData?.length || 0); i += chunkSize) {
        chunks.push(importedData.slice(i, i + chunkSize));
    }

    let processedCount = 0;
    let importResult = {
        added: 0,
        existed: 0,
        failed_rows: {}
    };

    chunks.forEach((chunk, index) => {
        const blob = new Blob([createCSV(chunk)], { type: 'text/csv' });

        let formData = new FormData();
        formData.append('file', blob, 'startups.csv');
        formData.append('is_public', form.isPublic ? 1 : 0);
        formData.append('trackers', JSON.stringify(form.trackers));
        formData.append('tags', JSON.stringify(form.tags));
        formData.append('portfolios', JSON.stringify(form.portfolios));

        inviteService.addMultipleStartups(formData).then(({data}) => {
            processedCount++;

            for (let key in data.result) {
                if (key === 'failed_rows') {
                    if (Object.keys(data.result[key]).length) {
                        for (let rowNumber in data.result[key]) {
                            importResult[key][parseInt(rowNumber) + (index * chunkSize)] = data.result[key][rowNumber];
                        }
                    }

                    continue;
                }

                importResult[key] = importResult[key] + data.result[key];
            }

            if (processedCount === chunks.length) {
                loading.value = false;
                showModal.value = false;
                emit('startupsAdded');

                failedAddStartupsModal.value.open(importResult, new Blob([createCSV(importedData)], { type: 'text/csv' }));
            }
        });
    })
}

function handleFileUpload() {
    file.value = upload.value.files[0] || null;
}

async function editTemplate() {
    if (!form.emailTemplate.subject.length) {
        const {data} = await companyService.getEmailTemplateEdit('startup-invite');

        form.emailTemplate.subject = data.subject
        form.emailTemplate.body = data.body
    }

    showModal.value = false;
    emailTemplateEditModal.value.open(form.emailTemplate);
}

function setTemplate(template) {
    form.emailTemplate = template;
}

function reset() {
    resetForm();

    trackers.value = [];
    tags.value = [];
    portfolios.value = [];
    file.value = null;
    fileErrors.value = [];
    multiple.value = false;
    loading.value = false;
    canInvite.value = false;
}

function open(url = '') {
    reset();
    form.url = url;

    getData();
}

defineExpose({
    open
});
</script>

<template>
    <div>
        <b-modal v-model="showModal" :title="`Add ${createStartupLabel('Startups')}`">
            <template v-if="allowMultiple && !store.getters.settings.disable_upload">
                <div class="input-radio mb-2">
                    <input type="radio" v-model="multiple" :value="false" id="singleAdd">
                    <label for="singleAdd">Add one {{ createStartupLabel('startup') }}</label>
                </div>

                <div class="input-radio">
                    <input type="radio" v-model="multiple" :value="true" id="multipleAdd">
                    <label for="multipleAdd">Upload multiple {{ createStartupLabel('startups') }} with .csv</label>
                </div>
            </template>

            <div v-if="multiple" class="form">
                <span v-if="fileErrors.length" class="form__error">{{fileErrors[0]}}</span>

                <p v-if="!file" class="modal-text mt-3">Download and use <a :href="baseURL + '/files/add_startup.csv'" target="_blank">this file</a> for best results</p>

                <div class="input-file mb-3">
                    <template v-if="!file">
                        <label for="input-file__upload" class="mr-2">Choose File</label>
                        <input
                            type="file"
                            ref="upload"
                            id="input-file__upload"
                            accept=".csv"
                            @change="handleFileUpload"
                        >
                    </template>

                    <div v-else class="tags-list tags-list--light">
                            <span class="tags-list__tag mt-3">
                                <a>{{file.name}}</a>
                                <a @click="file = null" class="tags-list__tag__close"></a>
                            </span>
                    </div>
                </div>
            </div>

            <template v-else>
                <div class="form mt-3">
                    <h5 class="heading5 heading5--secondary">{{ createStartupLabel('Startup') }} Name</h5>
                    <span v-if="errors.name.length" class="form__error">{{errors.name[0]}}</span>
                    <b-form-input class="form-control--mid mb-3" v-model="form.name"/>

                    <h5 class="heading5 heading5--secondary">{{ createStartupLabel('Startup') }} URL</h5>

                    <span v-if="errors.url.length" class="form__error">
                        {{errors.url[0]}}
                    </span>
                    <b-form-input class="form-control--mid mb-2" v-model="form.url"></b-form-input>

                    <p class="modal-text">{{ createStartupLabel('Startup') }} URLs are checked against existing {{ createStartupLabel('startups') }} on SwitchPitch to avoid duplication</p>

                    <template v-if="canInvite">
                        <h5 class="heading5 heading5--secondary">Invite {{ createStartupLabel('Startup') }} Via Email</h5>

                        <span v-if="errors.contactEmail.length" class="form__error">
                            {{errors.contactEmail[0]}}
                        </span>
                        <b-form-input class="form-control--mid mb-2" v-model="form.contactEmail"></b-form-input>

                        <p class="modal-text">Adding a contact email will invite them to join SwitchPitch</p>
                    </template>

                    <template v-if="form.customFields.length">

                        <div v-for="(field, index) in form.customFields" :key="index" class="mb-4">
                            <h5 class="heading5 heading5--secondary">{{field.field.name}}</h5>

                            <CustomFieldValue
                                :field="form.customFields[index].field"
                                :instantUpdate="false"
                                @valueChanged="(value) => customValueChanged(index, value)"
                            />
                            <span v-if="customFieldsErrors[index]?.length" class="error">{{customFieldsErrors[index][0]}}</span>
                        </div>

                    </template>
                </div>
            </template>

            <template v-if="store.getters.trackersList.length">
                <h5 class="heading5 heading5--secondary">Add To Trackers</h5>

                <TrackersSelect
                    v-model="trackers"
                    :trackers-list="$store.getters.trackersList"
                    :multiple="true"
                />
                <br>
            </template>

            <template v-if="showTags">
                <h5 class="heading5 heading5--secondary">Add Tags</h5>

                <TagsSelect
                    v-model="tags"
                />
            </template>

            <template v-if="!(store.getters.isEnterprise && (store.getters.isPfizerEnterprise || store.getters.isTravelersEnterprise || store.getters.isPMIEnterprise))">
                <br>
                <h5 class="heading5 heading5--secondary mb-2">{{ createStartupLabel('Startup') }} Visibility</h5>

                <div class="input-radio mb-2">
                    <input type="radio" v-model="form.isPublic" :value="true" id="public-startup">
                    <label for="public-startup">Public - Other SwitchPitch enterprise users will see this {{ createStartupLabel('startup') }}</label>
                </div>

                <div class="input-radio mb-4">
                    <input type="radio" v-model="form.isPublic" :value="false" id="private-startup">
                    <label for="private-startup">Private - Only users in your company account will see this {{ createStartupLabel('startup') }}</label>
                </div>
            </template>

            <template v-if="multiple && store.getters.isEnterprise && store.getters.isTravelersEnterprise && usersCanSoftTagging.includes(store.state.user.email)">
                <br>
                <h5 class="heading5 heading5--secondary mb-2">Auto-tagging</h5>

                <div class="input-radio mb-2">
                    <input type="radio" v-model="form.autoTagging" :value="true" id="auto-tagging-enabled">
                    <label for="auto-tagging-enabled">Enabled</label>
                </div>

                <div class="input-radio mb-4">
                    <input type="radio" v-model="form.autoTagging" :value="false" id="auto-tagging-disabled">
                    <label for="auto-tagging-disabled">Disabled</label>
                </div>
            </template>

            <template v-if="store.getters.isEcosystem">
                <h5 class="heading5 heading5--secondary">Add To Portfolios</h5>

                <PortfoliosSelect
                    v-model="portfolios"
                />
            </template>

            <template #modal-footer>
                <div class="buttons-wrapper">
                    <button
                        v-if="contactEmailEntered"
                        class="btn btn-bordered btn-lg"
                        @click="editTemplate"
                    >
                        Customize Invite
                    </button>

                    <PrimaryButton
                        :disabled="!canAdd"
                        :caption="contactEmailEntered ? 'Add & Invite' : 'Add'"
                        loadingCaption="Adding..."
                        :loading="loading"
                        @click="add"
                    />
                </div>
            </template>
        </b-modal>

        <EmailTemplateEdit
            ref="emailTemplateEditModal"
            @saved="setTemplate"
            @closed="showModal = true"
        />

        <FailedAddStartupsModal
            ref="failedAddStartupsModal"
        />

        <AddStartupsPreviewModal
            ref="previewModal"
            @apply="(fileData) => sendImport(fileData)"
        />
    </div>
</template>

<style scoped>

</style>
